import React, { useEffect, useRef } from 'react';
import './LandingPage.css';

/*
  Marketing landing page for ROBO TOKEN.
  Props:
    onGetStarted() — called by the "Register" / "Connect" buttons
    onSignIn()     — (optional) called by the "Sign In" link; defaults to onGetStarted
*/
const LandingPage = ({ onGetStarted = () => {}, onSignIn }) => {
  const rootRef = useRef(null);
  const goRegister = onGetStarted;
  const goSignIn = onSignIn || onGetStarted;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // nav scroll state
    const nav = root.querySelector('.rl-nav');
    const onScroll = () => nav && nav.classList.toggle('scrolled', window.scrollY > 20);
    window.addEventListener('scroll', onScroll);

    // mobile menu
    const toggle = root.querySelector('.nav-toggle');
    const links = root.querySelector('.nav-links');
    const toggleMenu = () => links && links.classList.toggle('open');
    const closeOnLink = (e) => { if (e.target.tagName === 'A' && links) links.classList.remove('open'); };
    if (toggle) toggle.addEventListener('click', toggleMenu);
    if (links) links.addEventListener('click', closeOnLink);

    // scroll reveal
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    root.querySelectorAll('.reveal').forEach((el) => io.observe(el));

    // count-up for mining range
    const cio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const el = e.target, to = +el.dataset.to, dur = 1100, t0 = performance.now();
        const tick = (t) => {
          const p = Math.min((t - t0) / dur, 1), v = Math.floor(p * to);
          el.textContent = v.toLocaleString('en-US');
          if (p < 1) requestAnimationFrame(tick); else el.textContent = to.toLocaleString('en-US');
        };
        requestAnimationFrame(tick);
        cio.unobserve(el);
      });
    }, { threshold: 0.5 });
    root.querySelectorAll('.count').forEach((el) => cio.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (toggle) toggle.removeEventListener('click', toggleMenu);
      if (links) links.removeEventListener('click', closeOnLink);
      io.disconnect();
      cio.disconnect();
    };
  }, []);

  return (
    <div className="robo-landing" ref={rootRef}>

      {/* NAV */}
      <header className="rl-nav">
        <div className="wrap nav-inner">
          <a className="brand" href="#top"><span className="mark">◈</span><b>ROBO</b><span className="tag">TOKEN</span></a>
          <nav className="nav-links">
            <a href="#model">The Model</a>
            <a href="#tokenomics">Tokenomics</a>
            <a href="#rewards">Rewards</a>
            <a href="#safeguards">Safeguards</a>
            <a href="#roadmap">Roadmap</a>
          </nav>
          <div className="nav-cta">
            <button className="rl-btn rl-btn-ghost rl-btn-sm" onClick={goSignIn}>Sign In</button>
            <button className="rl-btn rl-btn-primary rl-btn-sm" onClick={goRegister}>Connect &amp; Register</button>
            <button className="nav-toggle" aria-label="Menu">☰</button>
          </div>
        </div>
      </header>

      <main id="top">

        {/* HERO */}
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <span className="eyebrow">ROBO TOKEN · BEP-20 · BNB Chain</span>
              <h1>Community-Powered. <span className="accent">Elastic Supply.</span> Built to Last.</h1>
              <p className="lead">A next-generation digital asset with zero initial supply, mint-on-demand mechanics, and structured multi-level rewards — designed for transparent, community-driven growth.</p>
              <div className="hero-actions">
                <button className="rl-btn rl-btn-primary" onClick={goRegister}>Connect Wallet &amp; Register →</button>
                <a className="rl-btn rl-btn-ghost" href="#model">See how it works</a>
              </div>
              <div className="hero-stats">
                <div className="stat"><div className="n">0</div><div className="l">Initial supply</div></div>
                <div className="stat"><div className="n">10%</div><div className="l">Burn on every sell</div></div>
                <div className="stat"><div className="n">10</div><div className="l">Reward levels</div></div>
                <div className="stat"><div className="n">2×</div><div className="l">Withdrawal cap</div></div>
              </div>
            </div>
            <div className="core-wrap" aria-hidden="true">
              <div className="core-ring r1" />
              <div className="core-ring r2" />
              <div className="core-ring r3" />
              <div className="flow mint">▲ MINT ON BUY</div>
              <div className="core"><span className="sym">◈</span></div>
              <div className="flow burn">BURN ON SELL ▼</div>
            </div>
          </div>
        </section>

        {/* WHY CRYPTO */}
        <section className="divider-top" id="model">
          <div className="wrap">
            <span className="eyebrow reveal">The Digital Revolution</span>
            <h2 className="section-title reveal">Built on the strengths of crypto</h2>
            <p className="section-intro reveal">ROBO TOKEN runs on a blockchain that records ownership and transfers value without a central bank — fast, borderless, always on, with rules built into the network.</p>
            <div className="grid g4">
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><circle cx="5" cy="6" r="2" /><circle cx="19" cy="6" r="2" /><circle cx="5" cy="18" r="2" /><circle cx="19" cy="18" r="2" /><path d="M7 7l3 3M17 7l-3 3M7 17l3-3M17 17l-3-3" /></svg></div><h3>Decentralization</h3><p>No single company, bank, or government controls the network. Authority is shared, making the system hard to censor or shut down.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg></div><h3>Security</h3><p>Cryptography protects every transaction and proves ownership. Only the rightful holder can authorize a transfer.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></svg></div><h3>Global Reach</h3><p>ROBO can move across borders anytime — no bank hours, no traditional payment rails. Transfers are faster and more direct.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><path d="M3 6h18M3 12h18M3 18h18" /><circle cx="8" cy="6" r="1.6" fill="#fff" stroke="none" /><circle cx="15" cy="12" r="1.6" fill="#fff" stroke="none" /><circle cx="10" cy="18" r="1.6" fill="#fff" stroke="none" /></svg></div><h3>Transparency</h3><p>Every transaction is recorded on a public ledger anyone can verify, creating accountability and a clear supply history.</p></div>
            </div>
          </div>
        </section>

        {/* ROBO ADVANTAGE */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">The Advantage</span>
            <h2 className="section-title reveal">Why ROBO TOKEN is different</h2>
            <p className="section-intro reveal">It redefines digital-asset economics — eliminating pre-mining, insider advantage, and whale concentration through a fully on-chain, participation-driven model.</p>
            <div className="grid g2">
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 8v8" /></svg></div><h3>Zero Initial Supply</h3><p>Total supply starts at 0. Every token is minted transparently on-chain only when someone buys.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><path d="M12 3c2 3 4 5 4 8a4 4 0 0 1-8 0c0-1 .5-2 1-3 .5 2 2 2 2 0 0-2-1-3 1-5z" /></svg></div><h3>Auto Burn on Buy/Sell</h3><p>10% of sold tokens are permanently burned, structurally reinforcing scarcity over time.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8 11l8-4M8 13l8 4" /></svg></div><h3>Multi-Level Rewards</h3><p>A 10-tier team income structure incentivizes genuine, structured community growth.</p></div>
              <div className="card reveal"><div className="chip"><svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" /></svg></div><h3>Anti-Dump Protection</h3><p>Wallet limits and burn mechanics structurally discourage speculation and coordinated dumps.</p></div>
            </div>
          </div>
        </section>

        {/* MINT / BURN LOOP */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">Self-Regulating Supply</span>
            <h2 className="section-title reveal">Mint-on-demand &amp; burn mechanics</h2>
            <p className="section-intro reveal">Supply expands only with genuine demand and contracts automatically during sell activity — a self-regulating ecosystem with no artificial inflation or external intervention.</p>
            <div className="loop reveal">
              <div className="loop-node buy"><span className="arrow">▲</span><h3>Buy</h3><p>New tokens are minted on-chain in real time as demand arrives.</p></div>
              <div className="loop-mid"><span className="o">∞</span>elastic<br />equilibrium</div>
              <div className="loop-node sell"><span className="arrow">▼</span><h3>Sell</h3><p>Tokens are burned on sale, contracting supply and reinforcing scarcity.</p></div>
            </div>
          </div>
        </section>

        {/* HOW MINING WORKS */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">Dynamic Mining</span>
            <h2 className="section-title reveal">How mining ROBO works</h2>
            <p className="section-intro reveal">Mining isn't energy-intensive computation — it's dynamic supply generation tied directly to market activity. When you buy, you participate in growth and reward distribution.</p>
            <div className="steps">
              <div className="step reveal"><h3>Buy with BNB</h3><p>You initiate a purchase using BNB, signaling real demand for ROBO TOKEN.</p><div className="bar" /></div>
              <div className="step reveal"><h3>Real-time Minting</h3><p>The smart contract instantly mints new ROBO tokens on-chain in response.</p><div className="bar" /></div>
              <div className="step reveal"><h3>Rewards Distributed</h3><p>A portion of newly minted tokens funds the multi-level community reward system.</p><div className="bar" /></div>
              <div className="step reveal"><h3>Supply Grows</h3><p>Circulating supply expands organically, reflecting genuine demand and participation.</p><div className="bar" /></div>
            </div>
          </div>
        </section>

        {/* MINING RANGE */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">Mining Range</span>
            <h2 className="section-title reveal">Accessible to all, balanced by design</h2>
            <p className="section-intro reveal">Flexible BNB-to-ROBO swap limits keep participation open to everyone while preventing whale concentration.</p>
            <div className="range">
              <div className="range-card reveal"><div className="label">Minimum Mining Amount</div><div className="amount"><span>$</span><span className="count" data-to="10">10</span> BNB</div><p>Start the mining process with a low amount, making BNB-to-ROBO participation accessible to all users.</p></div>
              <div className="range-card reveal"><div className="label">Maximum Mining Amount</div><div className="amount"><span>$</span><span className="count" data-to="5000">5,000</span> BNB</div><p>Scale your BNB swap up to $5,000 per user ID, supporting significant — but bounded — mining activity.</p></div>
            </div>
          </div>
        </section>

        {/* TOKENOMICS */}
        <section className="divider-top" id="tokenomics">
          <div className="wrap">
            <span className="eyebrow reveal">Tokenomics</span>
            <h2 className="section-title reveal">Token allocation per purchase</h2>
            <p className="section-intro reveal">Every purchase is split transparently — funding the buyer, the community, and continuous deflationary reinforcement on every transaction.</p>
            <div className="alloc">
              <div className="donut reveal" aria-hidden="true"><div className="center"><div className="t">Per Purchase</div><div className="v">100%</div></div></div>
              <div className="legend">
                <div className="leg reveal"><span className="dot" style={{ background: 'var(--rl-pink)' }} /><span className="pct">60%</span><span className="desc">Minted directly to the purchaser</span></div>
                <div className="leg reveal"><span className="dot" style={{ background: 'var(--rl-violet)' }} /><span className="pct">15%</span><span className="desc">Team income pool — across 10 network levels</span></div>
                <div className="leg reveal"><span className="dot" style={{ background: 'var(--rl-cyan)' }} /><span className="pct">5%</span><span className="desc">Royalty income pool</span></div>
                <div className="leg reveal"><span className="dot" style={{ background: 'var(--rl-pink-2)' }} /><span className="pct">10%</span><span className="desc">Permanently burned</span></div>
                <div className="leg reveal"><span className="dot" style={{ background: 'var(--rl-neutral)' }} /><span className="pct">10%</span><span className="desc">Administrative &amp; operational support</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* TEAM INCOME */}
        <section className="divider-top" id="rewards">
          <div className="wrap">
            <span className="eyebrow reveal">Multi-Level Rewards</span>
            <h2 className="section-title reveal">10-level team income structure</h2>
            <p className="section-intro reveal">15% of every purchase flows through a structured multi-tier reward system, incentivizing community expansion across 10 network levels.</p>
            <table className="robo-table reveal">
              <thead><tr><th>Network Level</th><th>Allocation</th></tr></thead>
              <tbody>
                <tr><td>Level 1</td><td>5.00%</td></tr>
                <tr><td>Level 2</td><td>3.50%</td></tr>
                <tr><td>Level 3</td><td>2.00%</td></tr>
                <tr><td>Level 4</td><td>1.25%</td></tr>
                <tr><td>Level 5</td><td>0.75%</td></tr>
                <tr><td>Levels 6 – 10</td><td>0.50% each</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* ROYALTY */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">Royalty Income</span>
            <h2 className="section-title reveal">Three royalty tiers</h2>
            <p className="section-intro reveal">5% of every purchase feeds the Royalty Pool. Participants qualify across three tiers based on self-investment and verified monthly team volume.</p>
            <div className="grid g3">
              <div className="tier reveal"><div className="head"><span className="ic"><svg viewBox="0 0 24 24"><path d="M12 3l2.5 6 6.5.5-5 4.2 1.6 6.3L12 17l-5.6 3 1.6-6.3-5-4.2 6.5-.5z" /></svg></span><h3>Entry Royalty</h3></div><div className="body"><div className="row"><span>Self-investment</span><span>$1,000</span></div><div className="row"><span>Monthly team</span><span>$1,000</span></div><div className="row"><span>Members</span><span>2 × $500</span></div></div></div>
              <div className="tier reveal"><div className="head"><span className="ic"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M8 10h.01M16 10h.01M8 15c1 1.2 2.5 2 4 2s3-.8 4-2" /></svg></span><h3>Advanced Royalty</h3></div><div className="body"><div className="row"><span>Self-investment</span><span>$2,500</span></div><div className="row"><span>Monthly team</span><span>$2,500</span></div><div className="row"><span>Members</span><span>3 × $500</span></div></div></div>
              <div className="tier reveal"><div className="head"><span className="ic"><svg viewBox="0 0 24 24"><path d="M4 8l4 3 4-5 4 5 4-3v9H4z" /></svg></span><h3>Elite Royalty</h3></div><div className="body"><div className="row"><span>Self-investment</span><span>$5,000</span></div><div className="row"><span>Monthly team</span><span>$5,000</span></div><div className="row"><span>Members</span><span>4 × $500</span></div></div></div>
            </div>
          </div>
        </section>

        {/* SAFEGUARDS */}
        <section className="divider-top" id="safeguards">
          <div className="wrap">
            <span className="eyebrow reveal">Anti-Dump &amp; Liquidity Stability</span>
            <h2 className="section-title reveal">Structural safeguards</h2>
            <p className="section-intro reveal">Every sell enforces the 10% burn and 2× withdrawal cap. Reinvestment opens a fresh withdrawal cycle and keeps participation going.</p>
            <div className="modes">
              <div className="mode std reveal"><h3>Sell Rules</h3><ul><li>10% burn on admin buy/sells <b>(P2P transfers excluded)</b></li><li>Max withdrawal: <b>2× invested amount</b> per cycle</li><li>Reinvest to unlock the next withdrawal window</li></ul></div>
            </div>
            <div className="note-band reveal">No whale wallets exist at launch — concentration must be built gradually through participation. Wallet limits prevent mass accumulation, and system-wide burn on every sell continuously reduces supply.</div>
          </div>
        </section>

        {/* WHY BNB */}
        <section className="divider-top">
          <div className="wrap">
            <span className="eyebrow reveal">Infrastructure</span>
            <h2 className="section-title reveal">Why BNB Chain</h2>
            <p className="section-intro reveal">A robust, widely-adopted ecosystem that aligns with ROBO's vision for a transparent, community-driven asset.</p>
            <div className="grid g4">
              <div className="card reveal"><h3>Easy to Access</h3><p>A user-friendly, widely-adopted ecosystem makes ROBO readily accessible to a global community.</p></div>
              <div className="card reveal"><h3>Low Gas Fees</h3><p>Significantly lower transaction costs ensure efficiency and affordability for every operation and trade.</p></div>
              <div className="card reveal"><h3>Transparent Ledger</h3><p>A verifiable public ledger makes every ROBO transaction auditable, fostering trust and security.</p></div>
              <div className="card reveal"><h3>Growth Potential</h3><p>Alignment with the expanding BNB ecosystem taps into a vast network of dApps and users.</p></div>
            </div>
          </div>
        </section>

        {/* ROADMAP + RISK */}
        <section className="divider-top" id="roadmap">
          <div className="wrap">
            <span className="eyebrow reveal">Looking Ahead</span>
            <h2 className="section-title reveal">Roadmap &amp; risk disclosure</h2>
            <div className="road-grid">
              <div className="road reveal">
                <div className="phase"><span className="num">01</span><div><h3>DAO Governance Module</h3><p>Community voting on burn rates and allocations.</p></div></div>
                <div className="phase"><span className="num">02</span><div><h3>Staking Integration</h3><p>A yield-bearing participation layer.</p></div></div>
                <div className="phase"><span className="num">03</span><div><h3>NFT &amp; Platform Services</h3><p>Utility-based ecosystem expansion.</p></div></div>
              </div>
              <div className="risk reveal">
                <h3>⚠ Risk Disclosure</h3>
                <ul>
                  <li>Regulatory uncertainty across jurisdictions</li>
                  <li>Smart contract vulnerabilities</li>
                  <li>Adoption variability</li>
                  <li>Global market uncertainty</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="divider-top">
          <div className="wrap">
            <div className="cta-band reveal">
              <span className="eyebrow" style={{ justifyContent: 'center' }}>Join the ecosystem</span>
              <h2>Ready to mine ROBO?</h2>
              <p>Connect your wallet, enter a referral code, and start participating in a transparent, community-driven token economy.</p>
              <div className="hero-actions">
                <button className="rl-btn rl-btn-primary" onClick={goRegister}>Connect Wallet &amp; Register →</button>
                <a className="rl-btn rl-btn-ghost" href="#top">Back to top</a>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="rl-footer">
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-brand">
              <a className="brand" href="#top"><span className="mark">◈</span><b>ROBO</b><span className="tag">TOKEN</span></a>
              <p>An elastic-supply, community-powered digital asset on BNB Chain. Zero initial supply, mint-on-demand, structured rewards.</p>
            </div>
            <div className="foot-links">
              <div className="foot-col">
                <h4>Explore</h4>
                <a href="#model">The Model</a>
                <a href="#tokenomics">Tokenomics</a>
                <a href="#rewards">Rewards</a>
                <a href="#roadmap">Roadmap</a>
              </div>
              <div className="foot-col">
                <h4>Get Started</h4>
                <a onClick={goRegister}>Register</a>
                <a onClick={goSignIn}>Sign In</a>
                <a href="#safeguards">Sell Rules</a>
              </div>
            </div>
          </div>
          <p className="disclaimer">Risk disclosure: Digital assets are volatile and involve significant risk, including regulatory uncertainty across jurisdictions, potential smart-contract vulnerabilities, adoption variability, and broader market uncertainty. Nothing on this page is financial, investment, legal, or tax advice. Participate only with funds you can afford to lose, and do your own research.</p>
          <p className="copy">ROBO TOKEN © 2026 — Elastic Supply Digital Asset</p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;